// ============================================================
// FILE: SuburbPicker.js
// LOCATION: C:\gardenmarket\web\src\components\SuburbPicker.js
// ============================================================
// PURPOSE:
//   Dual-list (picklist) selector for service areas. Left pane =
//   available suburbs, right pane = selected. Arrow buttons move
//   items between panes.
//
// NOTES:
//   - Rows are tap-to-highlight, NOT ctrl+click multi-select, so
//     this works on touch devices. Highlighted rows are staged;
//     the arrow buttons commit the move.
//   - Double-click / double-tap a row to move it immediately.
//   - Panes are fixed height with internal scroll, so the section
//     never grows past ~300px no matter how many suburbs load.
//   - Below 640px the panes stack and the arrows become up/down.
//
// PROPS:
//   available   array of suburb objects { suburb_id, suburb_name,
//               postcode, distance_km? } — the candidate pool
//   selectedIds array of currently-selected suburb_id
//   getSuburb   fn(id) -> suburb object (or undefined); used to
//               label selected items that aren't in `available`
//   onChange    fn(nextSelectedIds)
// ============================================================

import { useState, useEffect, useMemo } from 'react';

function useNarrow(breakpoint = 640) {
    const [narrow, setNarrow] = useState(
        () => typeof window !== 'undefined' && window.innerWidth < breakpoint
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        const handler = (e) => setNarrow(e.matches);
        setNarrow(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', handler);
        else mq.addListener(handler);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handler);
            else mq.removeListener(handler);
        };
    }, [breakpoint]);
    return narrow;
}

const labelOf = (s) =>
    s && s.suburb_name
        ? [s.suburb_name, s.postcode].filter(Boolean).join(' ')
        : '';

export default function SuburbPicker({
    available = [],
    selectedIds = [],
    getSuburb = () => undefined,
    onChange = () => {},
}) {
    const narrow = useNarrow();
    const [filter, setFilter] = useState('');
    const [hlAvailable, setHlAvailable] = useState([]);
    const [hlSelected,  setHlSelected]  = useState([]);

    const selectedSet = useMemo(
        () => new Set(selectedIds.map(Number)),
        [selectedIds]
    );

    // Left pane: everything in the pool that isn't already selected,
    // narrowed by the filter box.
    const availableRows = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return available
            .filter(s => !selectedSet.has(Number(s.suburb_id)))
            .filter(s => !q || labelOf(s).toLowerCase().includes(q))
            .sort((a, b) => {
                const da = a.distance_km ?? 9999, db = b.distance_km ?? 9999;
                if (da !== db) return da - db;
                return labelOf(a).localeCompare(labelOf(b));
            });
    }, [available, selectedSet, filter]);

    // Right pane: selected IDs resolved to objects, alphabetical.
    const selectedRows = useMemo(() => {
        return selectedIds
            .map(id => getSuburb(id) || { suburb_id: id, suburb_name: '' })
            .sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
    }, [selectedIds, getSuburb]);

    const toggle = (list, setList, id) =>
        setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

    const addIds = (ids) => {
        if (ids.length === 0) return;
        const next = [...selectedIds];
        ids.forEach(id => { if (!next.includes(id)) next.push(id); });
        onChange(next);
        setHlAvailable([]);
    };

    const removeIds = (ids) => {
        if (ids.length === 0) return;
        const drop = new Set(ids.map(Number));
        onChange(selectedIds.filter(id => !drop.has(Number(id))));
        setHlSelected([]);
    };

    const arrowAdd    = narrow ? '▼' : '▶';
    const arrowRemove = narrow ? '▲' : '◀';

    const paneBody = (rows, highlighted, setHighlighted, onDouble, emptyText, showDist) => (
        <div style={styles.paneBody}>
            {rows.length === 0 && <div style={styles.empty}>{emptyText}</div>}
            {rows.map(s => {
                const id = s.suburb_id;
                const on = highlighted.includes(id);
                return (
                    <div
                        key={id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggle(highlighted, setHighlighted, id)}
                        onDoubleClick={() => onDouble(id)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggle(highlighted, setHighlighted, id);
                            }
                        }}
                        style={on ? styles.rowOn : styles.row}
                    >
                        <span>{labelOf(s) || `Suburb #${id}`}</span>
                        {showDist && s.distance_km > 0 && (
                            <span style={styles.dist}>{s.distance_km}km</span>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div>
            <div style={narrow ? styles.wrapNarrow : styles.wrap}>
                {/* ---------- Available ---------- */}
                <div style={styles.pane}>
                    <div style={styles.paneHead}>
                        Nearby suburbs
                        <span style={styles.count}>{availableRows.length}</span>
                    </div>
                    <input
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        placeholder="Filter this list..."
                        style={styles.filter}
                    />
                    {paneBody(
                        availableRows, hlAvailable, setHlAvailable,
                        (id) => addIds([id]),
                        filter ? 'No matches in the nearby list.' : 'No nearby suburbs found.',
                        true
                    )}
                </div>

                {/* ---------- Arrows ---------- */}
                <div style={narrow ? styles.arrowsNarrow : styles.arrows}>
                    <button type="button" style={styles.arrowBtn}
                        disabled={hlAvailable.length === 0}
                        title="Add highlighted"
                        onClick={() => addIds(hlAvailable)}>
                        {arrowAdd}
                    </button>
                    <button type="button" style={styles.arrowBtnSm}
                        disabled={availableRows.length === 0}
                        title="Add everything shown"
                        onClick={() => addIds(availableRows.map(s => s.suburb_id))}>
                        {arrowAdd}{arrowAdd}
                    </button>
                    <button type="button" style={styles.arrowBtn}
                        disabled={hlSelected.length === 0}
                        title="Remove highlighted"
                        onClick={() => removeIds(hlSelected)}>
                        {arrowRemove}
                    </button>
                    <button type="button" style={styles.arrowBtnSm}
                        disabled={selectedRows.length === 0}
                        title="Remove all"
                        onClick={() => removeIds(selectedRows.map(s => s.suburb_id))}>
                        {arrowRemove}{arrowRemove}
                    </button>
                </div>

                {/* ---------- Selected ---------- */}
                <div style={styles.pane}>
                    <div style={styles.paneHead}>
                        My service areas
                        <span style={styles.countOn}>{selectedRows.length}</span>
                    </div>
                    <div style={styles.headSpacer} />
                    {paneBody(
                        selectedRows, hlSelected, setHlSelected,
                        (id) => removeIds([id]),
                        'None yet — add suburbs from the left.',
                        false
                    )}
                </div>
            </div>

            <p style={styles.hint}>
                Tap to highlight, then use the arrows. Double-tap a suburb to move it straight across.
            </p>
        </div>
    );
}

const paneBase = {
    flex: 1,
    minWidth: 0,
    border: '1px solid #ddd',
    borderRadius: '8px',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const rowBase = {
    padding: '7px 10px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    borderBottom: '1px solid #f2f2f2',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    userSelect: 'none',
};

const arrowBtnBase = {
    border: '1px solid #ddd',
    background: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#2d6a4f',
    lineHeight: 1,
    padding: '7px 10px',
};

const styles = {
    wrap:        { display:'flex', gap:'10px', alignItems:'stretch' },
    wrapNarrow:  { display:'flex', gap:'10px', flexDirection:'column' },
    pane:        paneBase,
    paneHead:    { padding:'8px 10px', fontSize:'0.78rem', fontWeight:'bold', color:'#888', textTransform:'uppercase', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fafafa' },
    count:       { fontSize:'0.75rem', color:'#888', fontWeight:600 },
    countOn:     { fontSize:'0.75rem', color:'#2d6a4f', fontWeight:700 },
    filter:      { margin:'8px', padding:'6px 9px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'0.83rem', width:'calc(100% - 16px)', boxSizing:'border-box' },
    headSpacer:  { height:'41px' },   // keeps both panes' rows aligned
    paneBody:    { height:'240px', overflowY:'auto' },
    row:         rowBase,
    rowOn:       { ...rowBase, background:'#e8f5e9', color:'#2d6a4f', fontWeight:600 },
    dist:        { fontSize:'0.72rem', opacity:0.6, flexShrink:0 },
    empty:       { padding:'14px 10px', fontSize:'0.82rem', color:'#aaa', textAlign:'center' },
    arrows:      { display:'flex', flexDirection:'column', justifyContent:'center', gap:'8px' },
    arrowsNarrow:{ display:'flex', flexDirection:'row', justifyContent:'center', gap:'8px' },
    arrowBtn:    { ...arrowBtnBase, fontSize:'0.9rem' },
    arrowBtnSm:  { ...arrowBtnBase, fontSize:'0.7rem' },
    hint:        { fontSize:'0.8rem', color:'#999', marginTop:'10px', marginBottom:0 },
};
